import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  MapPin,
  ArrowRight,
  Plus,
  Pencil,
  Trash2,
  Route as RouteIcon,
  ChevronDown,
  Calendar,
  Store as StoreIcon,
  CheckCircle2,
  Circle,
} from "lucide-react";
import { toast } from "sonner";

const emptyForm = {
  name: "",
  owner_name: "",
  phone: "",
  address: "",
  store_type: "retail",
  lat: "",
  lng: "",
  notes: "",
};

const UNASSIGNED_KEY = "__unassigned__";

function dayLabel(d, today) {
  if (!d) return "Unscheduled";
  if (d === today) return "Today";
  const diff = Math.round((new Date(d) - new Date(today)) / 86400000);
  if (diff === 1) return "Tomorrow";
  if (diff > 1 && diff < 7) return `In ${diff} days`;
  if (diff < 0) return "Past";
  return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

export default function SalesmanStores() {
  const [stores, setStores] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [visitedToday, setVisitedToday] = useState(() => new Set());
  const [noOrderToday, setNoOrderToday] = useState(() => new Map()); // store_id -> reason_label
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [openGroups, setOpenGroups] = useState(() => new Set());

  const today = new Date().toISOString().slice(0, 10);

  const load = async () => {
    const [s, r, v, o, n] = await Promise.all([
      api.get("/stores"),
      api.get("/routes/my"),
      api.get("/visits"),
      api.get("/orders"),
      api.get("/no-order-reports", { params: { date_from: today, date_to: today } }),
    ]);
    setStores(s.data);
    setRoutes(r.data);
    const visited = new Set();
    for (const visit of v.data || []) {
      if ((visit.check_in_at || "").slice(0, 10) === today && visit.store_id) {
        visited.add(visit.store_id);
      }
    }
    for (const ord of o.data || []) {
      if ((ord.created_at || "").slice(0, 10) === today && ord.store_id) {
        visited.add(ord.store_id);
      }
    }
    const noOrderMap = new Map();
    for (const rep of n.data || []) {
      visited.add(rep.store_id);
      // Keep the most recent reason per store (data is sorted desc by created_at)
      if (!noOrderMap.has(rep.store_id)) {
        noOrderMap.set(rep.store_id, rep.reason_label);
      }
    }
    setVisitedToday(visited);
    setNoOrderToday(noOrderMap);
    // auto-open today's route by default (first time only)
    setOpenGroups((prev) => {
      if (prev.size > 0) return prev;
      const todays = r.data.find((x) => !x.scheduled_date || x.scheduled_date === today);
      return new Set(todays ? [todays.id] : r.data[0] ? [r.data[0].id] : []);
    });
  };

  useEffect(() => {
    load();
  }, []);

  // Group stores by route
  const groups = useMemo(() => {
    const storeMap = new Map(stores.map((s) => [s.id, s]));
    const seen = new Set();
    const result = [];
    for (const r of routes) {
      const items = (r.store_ids || [])
        .map((sid) => storeMap.get(sid))
        .filter(Boolean);
      items.forEach((s) => seen.add(s.id));
      result.push({
        key: r.id,
        title: r.name,
        scheduled_date: r.scheduled_date,
        stores: items,
        isRoute: true,
      });
    }
    const orphan = stores.filter((s) => !seen.has(s.id));
    if (orphan.length > 0) {
      result.push({
        key: UNASSIGNED_KEY,
        title: "Other stores",
        scheduled_date: null,
        stores: orphan,
        isRoute: false,
      });
    }
    return result;
  }, [stores, routes]);

  // Filter
  const filteredGroups = useMemo(() => {
    if (!q.trim()) return groups;
    const needle = q.toLowerCase();
    return groups
      .map((g) => ({
        ...g,
        stores: g.stores.filter((s) =>
          (s.name + " " + (s.address || "")).toLowerCase().includes(needle),
        ),
      }))
      .filter((g) => g.stores.length > 0);
  }, [groups, q]);

  // When searching, auto-expand all matching groups
  const expandedKeys = useMemo(() => {
    if (q.trim()) return new Set(filteredGroups.map((g) => g.key));
    return openGroups;
  }, [filteredGroups, openGroups, q]);

  const toggleGroup = (key) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const totalStores = stores.length;
  const totalRoutes = routes.length;

  const openNew = () => {
    setForm(emptyForm);
    setEditingId(null);
    setOpen(true);
  };

  const openEdit = (s, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setForm({
      name: s.name,
      owner_name: s.owner_name || "",
      phone: s.phone || "",
      address: s.address || "",
      store_type: s.store_type,
      lat: s.lat ?? "",
      lng: s.lng ?? "",
      notes: s.notes || "",
    });
    setEditingId(s.id);
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.address.trim()) {
      return toast.error("Name & address required");
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        lat: form.lat === "" || form.lat === null ? null : Number(form.lat),
        lng: form.lng === "" || form.lng === null ? null : Number(form.lng),
      };
      if (editingId) {
        await api.put(`/stores/${editingId}`, payload);
        toast.success("Store updated");
      } else {
        await api.post("/stores", payload);
        toast.success("Store added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    try {
      await api.delete(`/stores/${s.id}`);
      toast.success("Store deleted");
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Location not available");
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        setForm((f) => ({
          ...f,
          lat: pos.coords.latitude.toFixed(6),
          lng: pos.coords.longitude.toFixed(6),
        })),
      () => toast.error("Failed to get location"),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  return (
    <div className="space-y-4" data-testid="salesman-stores-list">
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="overline text-[color:var(--muted)]">By route</p>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Stores</h1>
          <p className="text-xs text-[color:var(--muted)] mt-1">
            {totalStores} shops · {totalRoutes} routes
          </p>
        </div>
        <Button
          onClick={openNew}
          className="h-11 rounded-xl bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white btn-tap"
          data-testid="salesman-add-store-button"
        >
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>

      <Input
        placeholder="Search shops…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="h-12"
        data-testid="stores-search-input"
      />

      <div className="space-y-3">
        {filteredGroups.length === 0 && (
          <div className="text-center text-[color:var(--muted)] py-10 rounded-xl border border-dashed border-[color:var(--border)]">
            {q ? "No shops match your search." : "No routes assigned. Plan a beat from the home tab."}
          </div>
        )}

        {filteredGroups.map((g) => {
          const expanded = expandedKeys.has(g.key);
          const isToday =
            g.isRoute && (!g.scheduled_date || g.scheduled_date === today);
          const visitedInGroup = g.stores.filter((s) => visitedToday.has(s.id)).length;
          const totalInGroup = g.stores.length;
          const allDone = totalInGroup > 0 && visitedInGroup === totalInGroup;
          return (
            <div
              key={g.key}
              className="rounded-xl border border-[color:var(--border)] bg-white ink-shadow overflow-hidden"
              data-testid={`route-group-${g.key}`}
            >
              <button
                type="button"
                onClick={() => toggleGroup(g.key)}
                className="w-full flex items-center gap-3 p-4 text-left btn-tap hover:bg-[color:var(--surface-2)]/40"
                data-testid={`route-group-toggle-${g.key}`}
              >
                <div
                  className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${
                    allDone
                      ? "bg-[color:var(--moss)]/15 text-[color:var(--moss)]"
                      : isToday
                      ? "bg-[color:var(--primary)]/10 text-[color:var(--primary)]"
                      : "bg-[color:var(--surface-2)] text-[color:var(--muted)]"
                  }`}
                >
                  {g.isRoute ? (
                    <RouteIcon className="h-5 w-5" />
                  ) : (
                    <StoreIcon className="h-5 w-5" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {g.isRoute && (
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide font-semibold ${
                          isToday
                            ? "bg-[color:var(--primary)]/10 text-[color:var(--primary)]"
                            : "bg-[color:var(--surface-2)] text-[color:var(--muted)]"
                        }`}
                      >
                        {dayLabel(g.scheduled_date, today)}
                      </span>
                    )}
                    {totalInGroup > 0 && (
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide font-semibold ${
                          allDone
                            ? "bg-[color:var(--moss)]/15 text-[color:var(--moss)]"
                            : visitedInGroup > 0
                            ? "bg-[color:var(--mustard)]/20 text-[#9A742E]"
                            : "bg-[color:var(--surface-2)] text-[color:var(--muted)]"
                        }`}
                        data-testid={`route-progress-${g.key}`}
                      >
                        {visitedInGroup}/{totalInGroup} visited
                      </span>
                    )}
                  </div>
                  <div className="font-display font-bold text-lg mt-0.5 truncate">
                    {g.title}
                  </div>
                  {totalInGroup > 0 && (
                    <div className="mt-2 h-1.5 rounded-full bg-[color:var(--surface-2)] overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          allDone
                            ? "bg-[color:var(--moss)]"
                            : "bg-[color:var(--primary)]"
                        }`}
                        style={{
                          width: `${Math.round((visitedInGroup / totalInGroup) * 100)}%`,
                        }}
                      />
                    </div>
                  )}
                  <div className="text-xs text-[color:var(--muted)] flex items-center gap-3 mt-1">
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {totalInGroup} shops
                    </span>
                    {g.scheduled_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {g.scheduled_date}
                      </span>
                    )}
                  </div>
                </div>
                <ChevronDown
                  className={`h-5 w-5 text-[color:var(--muted)] shrink-0 transition-transform ${
                    expanded ? "rotate-180" : ""
                  }`}
                />
              </button>

              {expanded && (
                <ul
                  className="divide-y divide-[color:var(--border)] border-t border-[color:var(--border)]"
                  data-testid={`route-group-list-${g.key}`}
                >
                  {g.stores.length === 0 && (
                    <li className="p-4 text-sm text-[color:var(--muted)] text-center">
                      No shops in this route yet.
                    </li>
                  )}
                  {g.stores.map((s, i) => {
                    const isVisited = visitedToday.has(s.id);
                    return (
                      <li
                        key={s.id}
                        className={`flex items-center gap-2 px-3 py-2.5 ${
                          isVisited ? "bg-[color:var(--moss)]/5" : ""
                        }`}
                        data-testid={`store-list-item-${s.id}`}
                      >
                        <Link
                          to={`/app/stores/${s.id}`}
                          className="flex items-center gap-3 flex-1 min-w-0"
                        >
                          {isVisited ? (
                            <span
                              className="h-8 w-8 rounded-full bg-[color:var(--moss)]/15 text-[color:var(--moss)] flex items-center justify-center shrink-0"
                              aria-label="Visited"
                              data-testid={`store-visited-${s.id}`}
                            >
                              <CheckCircle2 className="h-5 w-5" />
                            </span>
                          ) : (
                            <span className="h-8 w-8 rounded-full bg-[color:var(--surface-2)] text-[color:var(--muted)] flex items-center justify-center text-sm font-semibold shrink-0 relative">
                              <Circle className="h-5 w-5 absolute opacity-40" />
                              <span className="relative">{i + 1}</span>
                            </span>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate flex items-center gap-2">
                              <span className="truncate">{s.name}</span>
                              {isVisited && !noOrderToday.has(s.id) && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[color:var(--moss)]/15 text-[color:var(--moss)] uppercase tracking-wide font-semibold shrink-0">
                                  Visited
                                </span>
                              )}
                              {noOrderToday.has(s.id) && (
                                <span
                                  className="text-[9px] px-1.5 py-0.5 rounded-full bg-[color:var(--mustard)]/20 text-[#9A742E] uppercase tracking-wide font-semibold shrink-0 truncate max-w-[160px]"
                                  title={noOrderToday.get(s.id)}
                                  data-testid={`store-no-order-badge-${s.id}`}
                                >
                                  No order · {noOrderToday.get(s.id)}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-[color:var(--muted)] truncate flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {s.address}
                            </div>
                          </div>
                          <span
                            className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${
                              s.store_type === "wholesale"
                                ? "bg-[color:var(--mustard)]/20 text-[#9A742E]"
                                : "bg-[color:var(--moss)]/10 text-[color:var(--moss)]"
                            }`}
                          >
                            {s.store_type}
                          </span>
                          <ArrowRight className="h-4 w-4 text-[color:var(--muted)] shrink-0" />
                        </Link>
                        <div className="flex items-center gap-1 border-l border-[color:var(--border)] pl-1">
                          <button
                            onClick={(e) => openEdit(s, e)}
                            className="p-2 text-[color:var(--muted)] hover:text-[color:var(--ink)] rounded-md"
                            aria-label="Edit store"
                            data-testid={`edit-store-${s.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => remove(s, e)}
                            className="p-2 text-[color:var(--muted)] hover:text-[color:var(--primary)] rounded-md"
                            aria-label="Delete store"
                            data-testid={`delete-store-${s.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="salesman-store-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit store" : "New store"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Store name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="h-12"
                data-testid="salesman-store-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Owner</Label>
                <Input
                  value={form.owner_name}
                  onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
                  className="h-12"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="h-12"
                />
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <Textarea
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                data-testid="salesman-store-address-input"
              />
            </div>
            <div>
              <Label>Type</Label>
              <Select
                value={form.store_type}
                onValueChange={(v) => setForm({ ...form, store_type: v })}
              >
                <SelectTrigger className="h-12" data-testid="salesman-store-type-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="retail">Retail</SelectItem>
                  <SelectItem value="wholesale">Wholesale</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Latitude</Label>
                <Input
                  value={form.lat}
                  onChange={(e) => setForm({ ...form, lat: e.target.value })}
                  className="h-12"
                />
              </div>
              <div>
                <Label>Longitude</Label>
                <Input
                  value={form.lng}
                  onChange={(e) => setForm({ ...form, lng: e.target.value })}
                  className="h-12"
                />
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={useMyLocation}
              className="w-full h-12"
              data-testid="salesman-use-location-button"
            >
              <MapPin className="h-4 w-4 mr-2" /> Use my location
            </Button>
          </div>
          <DialogFooter>
            <Button
              onClick={save}
              disabled={saving}
              className="w-full h-12 rounded-xl bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white"
              data-testid="salesman-save-store-button"
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
